import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type { ApiError } from "./clinicalDiagramTypes";
import { uploadToS3 } from "../../services/common";

/**
 * Payload for requesting a pre-signed upload URL.
 */
export type UploadPayload = {
  s3_key_name: string;
};

/**
 * Fetches a pre-signed S3 URL for uploading a file.
 *
 * Args:
 *   payload: UploadPayload containing the S3 key name.
 *
 * Returns:
 *   { unwrap: () => Promise<{ pre_signed_url?: string } | undefined> }:
 *     RTK Query mutation result wrapper.
 */
export type FetchS3ObjectFn = (payload: UploadPayload) => {
  unwrap: () => Promise<{ pre_signed_url?: string } | undefined>;
};

/**
 * Parameters for uploading a clinical diagram file to S3.
 */
export type UploadFileParams = {
  file: File;
  fetchS3Object: FetchS3ObjectFn;
  keyName: string;
};

/**
 * Normalizes API error shapes into a user-facing message string.
 *
 * Args:
 *   error: Unknown error thrown by mutation/query handlers.
 *   fallback: Default message when no detail is available.
 *
 * Returns:
 *   string: The resolved error message.
 */
export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const apiError = error as ApiError | FetchBaseQueryError | undefined;
  if (!apiError) {
    return fallback;
  }

  if (typeof (apiError as ApiError).message === "string") {
    return (apiError as ApiError).message as string;
  }

  if (typeof (apiError as ApiError).data === "object") {
    const detail = (apiError as ApiError).data?.detail;
    if (typeof detail === "string") {
      return detail;
    }
  }

  return fallback;
};

/**
 * Uploads a file to S3 using a pre-signed URL and returns the S3 key.
 *
 * Args:
 *   file: File to upload.
 *   fetchS3Object: Mutation function to request a pre-signed URL.
 *   keyName: S3 key name for the uploaded object.
 *
 * Returns:
 *   Promise<string | undefined>: The key name if upload succeeds, otherwise undefined.
 *
 * Raises:
 *   Any errors thrown by the underlying S3 pre-sign or upload calls.
 */
export const uploadClinicalDiagramFile = async ({
  file,
  fetchS3Object,
  keyName,
}: UploadFileParams): Promise<string | undefined> => {
  const response = await fetchS3Object({ s3_key_name: keyName }).unwrap();
  if (!response?.pre_signed_url) {
    return undefined;
  }

  const uploadResponse = await uploadToS3({ url: response.pre_signed_url, file });
  return uploadResponse ? keyName : undefined;
};
